import React from 'react';
import { cn } from '../lib/utils';
import { TrendingUp, TrendingDown, Minus, type LucideIcon } from 'lucide-react';

export interface SummaryCardTrend {
  direction: 'up' | 'down' | 'flat';
  value: string;
  label?: string;
}

export interface SummaryCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  value?: string | number;
  subtitle?: string;
  description?: string;
  icon?: LucideIcon;
  trend?: SummaryCardTrend;
  /** Pass child elements for custom mini-visualizations */
  children?: React.ReactNode;
}

const trendConfig: Record<string, { icon: typeof TrendingUp; color: string }> = {
  up: { icon: TrendingUp, color: 'text-emerald-600' },
  down: { icon: TrendingDown, color: 'text-red-600' },
  flat: { icon: Minus, color: 'text-slate-400' },
};

function SummaryCard({
  title,
  value,
  subtitle,
  description,
  icon: Icon,
  trend,
  children,
  className,
  ...props
}: SummaryCardProps) {
  const subText = subtitle || description;

  return (
    <div
      className={cn(
        'relative min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition-interactive duration-200 ease-out',
        className,
      )}
      {...props}
    >
      <div className="mb-3 flex min-w-0 flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {Icon && (
            <Icon className="h-4 w-4 shrink-0 text-statura-green-accessible" aria-hidden="true" />
          )}
          <h4 className="min-w-0 break-words font-body text-eyebrow uppercase text-text-muted">
            {title}
          </h4>
        </div>
        {trend &&
          trendConfig[trend.direction] &&
          (() => {
            const tc = trendConfig[trend.direction]!;
            return (
              <div className={cn('flex shrink-0 items-center gap-1', tc.color)}>
                {React.createElement(tc.icon, {
                  className: 'h-3.5 w-3.5 shrink-0',
                })}
                <span className="text-xs font-semibold">{trend.label || trend.value}</span>
              </div>
            );
          })()}
      </div>

      {value !== undefined && (
        <p className="min-w-0 break-words font-body text-metric text-text-heading">{value}</p>
      )}

      {subText && (
        <p className="mt-1 min-w-0 break-words font-body text-caption text-text-muted">{subText}</p>
      )}

      {children && <div className="mt-3 min-w-0">{children}</div>}
    </div>
  );
}

/** Grid wrapper for laying out multiple SummaryCards */
function SummaryCardGrid({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export { SummaryCard, SummaryCardGrid };
